import {
  IVaultItemCounts,
  VaultNode,
  VaultSortOption,
} from "./VaultPage.types";

const DATE_LOCALE = "en-GB";
const TIME_LOCALE = "en-US";

const dateFormatter = new Intl.DateTimeFormat(DATE_LOCALE, {
  day: "numeric",
  month: "short",
  year: "numeric",
});

const timeFormatter = new Intl.DateTimeFormat(TIME_LOCALE, {
  hour: "numeric",
  minute: "2-digit",
});

export const formatVaultTimestamp = (date: Date): string =>
  `${dateFormatter.format(date)}, ${timeFormatter.format(date)}`;

export const countVaultItems = (nodes: VaultNode[]): IVaultItemCounts =>
  nodes.reduce<IVaultItemCounts>(
    (totals, node) => {
      if (node.kind === "file") {
        return { folders: totals.folders, files: totals.files + 1 };
      }
      const childTotals = countVaultItems(node.children);
      return {
        folders: totals.folders + 1 + childTotals.folders,
        files: totals.files + childTotals.files,
      };
    },
    { folders: 0, files: 0 },
  );

export const collectFolderIds = (nodes: VaultNode[]): string[] =>
  nodes.flatMap((node) =>
    node.kind === "folder" ? [node.id, ...collectFolderIds(node.children)] : [],
  );

const compareByName = (first: VaultNode, second: VaultNode): number =>
  first.name.localeCompare(second.name);

const compareByRecency = (first: VaultNode, second: VaultNode): number =>
  second.modifiedAt.getTime() - first.modifiedAt.getTime();

const compareFoldersFirst = (first: VaultNode, second: VaultNode): number => {
  if (first.kind === second.kind) {
    return compareByName(first, second);
  }
  return first.kind === "folder" ? -1 : 1;
};

const COMPARATORS: Record<
  VaultSortOption,
  (first: VaultNode, second: VaultNode) => number
> = {
  foldersFirst: compareFoldersFirst,
  name: compareByName,
  recentlyModified: compareByRecency,
};

export const sortVaultNodes = (
  nodes: VaultNode[],
  sortOption: VaultSortOption,
): VaultNode[] =>
  [...nodes]
    .sort(COMPARATORS[sortOption])
    .map((node) =>
      node.kind === "folder"
        ? { ...node, children: sortVaultNodes(node.children, sortOption) }
        : node,
    );

export const pluralize = (
  count: number,
  singular: string,
  plural = `${singular}s`,
): string => `${count} ${count === 1 ? singular : plural}`;
